import { useNavigate } from 'react-router-dom'
import {
  EOYBookkeepingFollowupSubstep,
  EOYBookkeepingSubstep,
  EOYBookkeepingSubstepId,
  fetchUserYearEndModuleStatuses,
  FormFilingJobSubstep,
  TQSubstep,
  TQSubstepId,
  upsertUserYearEndModuleStatus,
  YearEndModuleStatusOptions,
  YearEndModuleStatusRawOptions,
  YearEndModuleSubstepId,
  YearEndModuleType,
} from './yearEndModuleStatus.slice'
import { useReselector } from '../../utils/sharedHooks'
import {
  FETCH_ANNUAL_TAX_FILING_FORMS_KEY,
  FETCH_TAXFYLE_EXTENSION_JOB_URL_KEY,
  FETCH_TAXFYLE_FORM_JOB_URL_KEY,
  fetchAnnualTaxFilingFormsIfNeeded,
  fetchTaxFyleExtensionJobUrl,
  fetchTaxFyleJobUrl,
  TaxfyleJobStatus,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.slice'
import { fetchAnnualTaxFilingsIfNeeded } from '@/features/taxes/annual-taxes/annualTaxFilings.slice'
import {
  useOpenTaxfylePortal,
  type TaxfylePortal,
} from '@/features/taxes/annual-taxes/useOpenTaxfylePortal'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'
import { selectUserDocumentCategoryByInternalName } from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import {
  selectAnnualTaxFilingForCurrentTaxYear,
  selectIsBeforeExtensionDeadline,
} from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import {
  select1040FormForYear,
  select1120sFormForYear,
} from '@/features/taxes/annual-taxes/annualTaxFilingForms.selector'
import {
  FETCH_ANNUAL_TAX_OUTCOMES_KEY,
  fetchAnnualTaxOutcomesIfNeeded,
  selectAnnualTaxOutcomesByFormId,
} from '@/features/taxes/annual-taxes/annualTaxOutcome.slice'
import { AnnualTax1040WrapUpModal } from '@/features/taxes/annual-taxes/components/AnnualTaxWrapUp'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { Loader } from '@/componentsV1/BaseComponents'
import {
  selectCurrentAnnualTaxYear,
  selectTaxDetailsByYear,
} from '../admin/annual-tax-details/annualTaxDetails.selector'

type LastWorkedOnSubsteps = EOYBookkeepingSubstepId | TQSubstepId
const lastWorkedOnSubsteps: LastWorkedOnSubsteps[] = [
  ...Object.values(EOYBookkeepingSubstep),
  ...Object.values(TQSubstep),
]

const lastWorkedOnSubstepsMap: Record<
  LastWorkedOnSubsteps,
  string | ((taxYear?: string) => string)
> = {
  [EOYBookkeepingSubstep.disconnectedAccounts]: 'Review disconnected accounts',
  [EOYBookkeepingSubstep.uploadMissingStatements]:
    'Upload missing bank statements',
  [EOYBookkeepingSubstep.unclarifiedTransactions]:
    'Review “Uncategorized” transactions',
  [EOYBookkeepingSubstep.otherIncome]: 'Review “Other Income” transactions',
  [EOYBookkeepingSubstep.otherExpenses]: 'Review “Other Expenses” transactions',
  [EOYBookkeepingSubstep.personalAccountBusinessIncome]:
    'Business income in personal accounts',
  [EOYBookkeepingSubstep.personalAccountBusinessExpense]:
    'Business expenses in personal accounts',
  [EOYBookkeepingSubstep.ownerInvestments]: 'Review owner’s investments',
  [EOYBookkeepingSubstep.ownerDistributions]: 'Review owner’s distributions',
  [EOYBookkeepingSubstep.yearEndDocs]: 'Upload year-end documents',
  [EOYBookkeepingSubstep.initialSubmissionReview]: 'Review and submit',
  [TQSubstep.checkYourDetails]: 'Check your details',
  [TQSubstep.tqBigChanges]: (taxYear) => {
    return taxYear ? `Changes in ${taxYear}` : 'Changes this tax year'
  },
  [TQSubstep.tqDeductionsCredits]: 'Deductions and Credits',
  [TQSubstep.tqIncomeLiabilities]: 'Non-therapy income and liabilities',
  [TQSubstep.tqUploadDocuments]: 'Upload documents',
  [TQSubstep.tqAboutYourBusiness]: 'About your business',
  [TQSubstep.tqMissingQtePayments]: 'Missing QTE payments',
}

export const getSubstepURL = (
  taxYear?: string | null,
  substepIdentifier?: YearEndModuleSubstepId | null
): string | null => {
  if (!substepIdentifier || !taxYear) {
    return null
  }
  switch (substepIdentifier) {
    case EOYBookkeepingSubstep.disconnectedAccounts:
      return `/${taxYear}/review-disconnected-accounts`
    case EOYBookkeepingSubstep.uploadMissingStatements:
      return `/${taxYear}/upload-missing-bank-statements`
    case EOYBookkeepingSubstep.unclarifiedTransactions:
      return `/${taxYear}/categorize-transactions`
    case EOYBookkeepingSubstep.otherIncome:
      return `/${taxYear}/other-income`
    case EOYBookkeepingSubstep.otherExpenses:
      return `/${taxYear}/other-expenses`
    case EOYBookkeepingSubstep.personalAccountBusinessIncome:
      return `/${taxYear}/business-income-in-personal`
    case EOYBookkeepingSubstep.personalAccountBusinessExpense:
      return `/${taxYear}/business-expenses-in-personal`
    case EOYBookkeepingSubstep.ownerInvestments:
      return `/${taxYear}/review-owners-investments`
    case EOYBookkeepingSubstep.ownerDistributions:
      return `/${taxYear}/review-owners-distributions`
    case EOYBookkeepingSubstep.yearEndDocs:
      return `/${taxYear}/upload-documents-intro`
    case EOYBookkeepingSubstep.initialSubmissionReview:
      return `/${taxYear}/first-submission`
    case EOYBookkeepingFollowupSubstep.userFollowup:
      return `/${taxYear}/follow-up`
    case EOYBookkeepingFollowupSubstep.finalUserApproval:
      return `/${taxYear}/final-review-intro`
    default:
      return null
  }
}

export const getSubstepIdentifierLastWorkedOnCopy = (
  status: YearEndModuleStatusOptions,
  substepIdentifier: YearEndModuleSubstepId | null | undefined,
  taxYear?: string
): string | null => {
  const id = substepIdentifier as LastWorkedOnSubsteps
  if (
    ![
      YearEndModuleStatusOptions.inProgress,
      YearEndModuleStatusOptions.overdue,
    ].includes(status) ||
    !substepIdentifier ||
    !lastWorkedOnSubsteps.includes(id)
  ) {
    return null
  }
  const copy = lastWorkedOnSubstepsMap[id]
  if (typeof copy === 'function') {
    return copy(taxYear)
  }
  return copy
}

const getFormOrFilingId = (
  moduleType: YearEndModuleType | null,
  formId1040?: number,
  formId1120S?: number,
  filingId?: number
) => {
  if (!moduleType) {
    return {}
  }
  if (moduleType === YearEndModuleType.fileExtensionRequest) {
    return {
      filingId,
    }
  } else if (moduleType === YearEndModuleType.file1120S) {
    return {
      formId: formId1120S,
    }
  } else if (moduleType === YearEndModuleType.file1040) {
    return {
      formId: formId1040,
    }
  }
  // Only return valid form/filing id for the module types that need it
  return {}
}

const isTaxfyleLinkUnavailable = (
  moduleType: YearEndModuleType | null,
  status: YearEndModuleStatusOptions | null | undefined,
  taxfyleJobId: string | null | undefined,
  taxfyleJobStatus: TaxfyleJobStatus | null | undefined
) => {
  if (!moduleType || !status || !taxfyleJobStatus) {
    return true
  }
  const moduleIsNotTaxfyleJob =
    moduleType &&
    ![
      YearEndModuleType.fileExtensionRequest,
      YearEndModuleType.file1040,
      YearEndModuleType.file1120S,
    ].includes(moduleType)
  const statusHasNoJobUrl = status === YearEndModuleStatusOptions.upcoming
  return (
    moduleIsNotTaxfyleJob ||
    statusHasNoJobUrl ||
    !taxfyleJobId ||
    taxfyleJobStatus === TaxfyleJobStatus.underConstruction
  )
}

export const useYearEndTaxfylePortal = ({
  moduleType,
  status,
}: {
  moduleType: YearEndModuleType | null
  status: YearEndModuleStatusOptions | null | undefined
}): TaxfylePortal & { available: boolean } => {
  const fetchApiCall = useMemo(
    () =>
      moduleType === YearEndModuleType.fileExtensionRequest
        ? fetchTaxFyleExtensionJobUrl
        : fetchTaxFyleJobUrl,
    [moduleType]
  )

  const filing = useReselector(selectAnnualTaxFilingForCurrentTaxYear)
  const form1040 = useReselector(select1040FormForYear, filing?.year)
  const form1120S = useReselector(select1120sFormForYear, filing?.year)

  const { formId, filingId } = getFormOrFilingId(
    moduleType,
    form1040?.id,
    form1120S?.id,
    filing?.id
  )
  const linkId = formId || filingId

  const available = useMemo(() => {
    let taxfyleJobId = null
    let taxfyleJobStatus = null
    if (moduleType === YearEndModuleType.fileExtensionRequest) {
      taxfyleJobId = filing?.extensionTaxfyleJobId
      taxfyleJobStatus = filing?.extensionTaxfyleJob?.jobStatus
    } else if (moduleType === YearEndModuleType.file1040) {
      taxfyleJobId = form1040?.taxfyleJob?.jobId
      taxfyleJobStatus = form1040?.taxfyleJob?.jobStatus
    } else if (moduleType === YearEndModuleType.file1120S) {
      taxfyleJobId = form1120S?.taxfyleJob?.jobId
      taxfyleJobStatus = form1120S?.taxfyleJob?.jobStatus
    }
    return !isTaxfyleLinkUnavailable(
      moduleType,
      status,
      taxfyleJobId,
      taxfyleJobStatus
    )
  }, [
    filing?.extensionTaxfyleJobId,
    form1040?.taxfyleJob?.jobId,
    form1120S?.taxfyleJob?.jobId,
    moduleType,
    status,
    filing?.extensionTaxfyleJob?.jobStatus,
    form1040?.taxfyleJob?.jobStatus,
    form1120S?.taxfyleJob?.jobStatus,
  ])

  const fetchLink = useCallback(
    () => fetchApiCall(linkId),
    [fetchApiCall, linkId]
  )
  const fetchKey =
    moduleType === YearEndModuleType.fileExtensionRequest
      ? FETCH_TAXFYLE_EXTENSION_JOB_URL_KEY(filingId)
      : FETCH_TAXFYLE_FORM_JOB_URL_KEY(formId)
  const portal = useOpenTaxfylePortal(fetchLink, fetchKey, { available })

  return { ...portal, available }
}

export const useModuleActionButtonBehavior = (
  moduleType: YearEndModuleType | null,
  status: YearEndModuleStatusOptions | null,
  substepURL?: string | null,
  substepId?: YearEndModuleSubstepId | null
): {
  onActionClick: () => void
  modal?: ReactNode
  loading?: boolean
  error?: string | null
} => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [modalOpen, setModalOpen] = useState({
    verifyTaxEntity: false,
    update1040PaymentInfo: false,
  })
  const cp261DocumentCategory = useReselector(
    selectUserDocumentCategoryByInternalName,
    UserDocumentCategoryIdentifier.cp261Notice
  )
  const filing = useReselector(selectAnnualTaxFilingForCurrentTaxYear)
  const form1040 = useReselector(select1040FormForYear, filing?.year)
  const outcomes = useReselector(selectAnnualTaxOutcomesByFormId, form1040?.id)
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const taxDetails = useReselector(selectTaxDetailsByYear, currentTaxYear)

  useEffect(() => {
    dispatch(fetchAnnualTaxFilingsIfNeeded())
    dispatch(fetchAnnualTaxFilingFormsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    if (form1040?.id) {
      dispatch(fetchAnnualTaxOutcomesIfNeeded(form1040.id, true))
    }
  }, [dispatch, form1040?.id])
  const isFetchingTaxOutcomes = useReselector(selectIsFetchingForKeys, [
    FETCH_ANNUAL_TAX_FILING_FORMS_KEY,
    FETCH_ANNUAL_TAX_OUTCOMES_KEY,
  ])

  const portal = useYearEndTaxfylePortal({ moduleType, status })

  if (!moduleType) {
    return { onActionClick: () => {} }
  }

  switch (moduleType) {
    case YearEndModuleType.verifyTaxEntity: {
      const onUpload = () => {
        // may want to move this to backend
        upsertUserYearEndModuleStatus({
          moduleType: YearEndModuleType.verifyTaxEntity,
          status: YearEndModuleStatusOptions.waitingOnHeard,
        })(dispatch)
      }
      return {
        onActionClick: () => {
          setModalOpen({ ...modalOpen, verifyTaxEntity: true })
        },
        modal: cp261DocumentCategory?.type ? (
          <FileUploadModal
            open={modalOpen.verifyTaxEntity}
            close={() => setModalOpen({ ...modalOpen, verifyTaxEntity: false })}
            userFacing
            documentType={cp261DocumentCategory.type}
            categoryId={cp261DocumentCategory.id}
            setUploadedFile={onUpload}
          />
        ) : null,
      }
    }
    case YearEndModuleType.taxQuestionnaire:
      return {
        onActionClick: () => navigate('/taxes/annual/tax_checklist/'),
      }
    case YearEndModuleType.submitExtensionRequest:
      return {
        onActionClick: () => navigate('/taxes/annual/extension_request'),
      }
    case YearEndModuleType.eoyBookkeeping:
      return {
        onActionClick: () =>
          navigate(
            `/taxes/annual/complete-year-end-bookkeeping${substepURL ? substepURL : ''}`
          ),
      }
    case YearEndModuleType.file1099Nec: {
      // handled within card
      return {
        onActionClick: () => undefined,
      }
    }
    case YearEndModuleType.eoyBookkeepingFollowup:
      return {
        onActionClick: () =>
          navigate(
            `/taxes/annual/review-and-close-books${substepURL ? substepURL : ''}`
          ),
      }
    case YearEndModuleType.fileExtensionRequest:
    case YearEndModuleType.file1040: {
      if (substepId === FormFilingJobSubstep.updatePaymentInfo) {
        if (isFetchingTaxOutcomes) {
          return {
            onActionClick: () => {
              setModalOpen({ ...modalOpen, update1040PaymentInfo: true })
            },
            modal: <Loader />,
          }
        }
        const filteredOutcomes = outcomes?.filter((outcome) =>
          moduleType === YearEndModuleType.fileExtensionRequest
            ? outcome.isForExtension === true
            : outcome.isForExtension !== true
        )
        return {
          onActionClick: () => {
            setModalOpen({ ...modalOpen, update1040PaymentInfo: true })
          },
          modal: filteredOutcomes && (
            <AnnualTax1040WrapUpModal
              modalOpen={modalOpen.update1040PaymentInfo}
              onClose={() => {
                setModalOpen({ ...modalOpen, update1040PaymentInfo: false })
                dispatch(fetchUserYearEndModuleStatuses())
              }}
              year={filing?.year}
              outcomes={filteredOutcomes}
              extensionModal={
                moduleType === YearEndModuleType.fileExtensionRequest
              }
              paymentDueDate={taxDetails?.form_4868_irs_due_date}
            />
          ),
        }
      }
      return {
        onActionClick: () => {
          void portal.open()
        },
        loading: portal.loading,
        error: portal.error,
      }
    }
    case YearEndModuleType.file1120S: {
      return {
        onActionClick: () => {
          void portal.open()
        },
        loading: portal.loading,
        error: portal.error,
      }
    }
    default:
      return moduleType satisfies never
  }
}

export const getModuleComplementaryText = (moduleType: YearEndModuleType) => {
  switch (moduleType) {
    case YearEndModuleType.fileExtensionRequest:
    case YearEndModuleType.file1120S:
    case YearEndModuleType.file1040:
      return ', depending on how quickly you respond to your tax preparer.'
    case YearEndModuleType.eoyBookkeepingFollowup:
      return ', depending on how quickly you respond to your bookkeeper.'
    default:
      return ''
  }
}

export const getYearModuleStatusRawOptionsTag = (
  status: YearEndModuleStatusRawOptions
) => {
  switch (status) {
    case YearEndModuleStatusRawOptions.upNext:
      return 'Not Started'
    case YearEndModuleStatusRawOptions.earlyStart:
      return 'Early Start'
    case YearEndModuleStatusRawOptions.inProgress:
      return 'In Progress'
    case YearEndModuleStatusRawOptions.overdue:
      return 'Overdue'
    case YearEndModuleStatusRawOptions.missed:
      return 'Missed'
    case YearEndModuleStatusRawOptions.upcoming:
      return 'Blocked'
    case YearEndModuleStatusRawOptions.waitingOnHeard:
      return 'Waiting On Heard/Taxfyle'
    case YearEndModuleStatusRawOptions.actionRequired:
      return 'Waiting on Therapist'
    case YearEndModuleStatusRawOptions.complete:
      return 'Complete'
    case YearEndModuleStatusRawOptions.cancelled:
      return 'Cancelled'
    case YearEndModuleStatusRawOptions.notApplicable:
      return 'Not Applicable'
    case YearEndModuleStatusRawOptions.requiresKickoffSurvey:
      return 'Blocked - No TSK'
    default:
      return status satisfies never
  }
}

export const useModuleWarningText = (daysUntilDue: number) => {
  const isBeforeExtensionDeadline = useReselector(
    selectIsBeforeExtensionDeadline
  )

  let warningText = ''

  if (daysUntilDue >= 0 && !isBeforeExtensionDeadline) {
    warningText =
      'After this date, we may not be able to file ahead of your extended deadline. Your return could be filed late and you may owe penalties and interest.'
  } else if (daysUntilDue >= 0) {
    warningText = "After that, you'll need to file an extension for your taxes."
  } else if (!isBeforeExtensionDeadline) {
    warningText =
      'We may not be able to file ahead of your extended deadline. Completing this ASAP reduces your risk of late filing penalties and interest.'
  } else {
    warningText =
      'If you do not complete this ASAP, you will need to file an extension for your taxes.'
  }

  return warningText
}
